import React from "react";
import { useTranslation } from "react-i18next";
import styles from "../../../pages/admin/css/IndividualReport.module.css";

/**
 * Props:
 *  - data            {Array}   — rows to render
 *  - columns         {Array}   — [{ key, label, title?, center?, mono?, note?, ref? }]
 *                                  `title` = full tooltip shown on hover when label is abbreviated
 *  - tableColsPrefix {string}  — i18n prefix for type cell translations
 *  - title           {string}  — optional section label above the table
 *  - tokens          {object}  — MEM (yellow) or REV (pink) color tokens
 *  - totalItems      {number}  — shown in pagination footer
 *  - totalPages      {number}  — pass 1 or omit to hide pagination
 *  - currentPage     {number}  — active page (default 1)
 *  - onPage          {fn}      — (page) => void
 *  - onRowClick      {fn}      — (row) => void
 */
export const QuranReportTable = ({
  data,
  columns,
  tableColsPrefix,
  title,
  tokens,
  totalItems,
  totalPages,
  currentPage = 1,
  onPage,
  onRowClick,
}) => {
  const { t } = useTranslation();

  const thBg      = tokens?.thBg      ?? "#F5F4EF";
  const thColor   = tokens?.text      ?? "#444";
  const accent    = tokens?.accent    ?? "#666";
  const pagActive = tokens?.pagActive ?? "#555";
  const border    = tokens?.border    ?? "#EEE";

  const showPagination = totalPages > 1;
  const visible = Array.from({ length: totalPages || 0 }, (_, i) => i + 1)
    .filter(p => Math.abs(p - currentPage) <= 2);

  const cx = (...cls) => cls.filter(Boolean).join(" ");

  return (
    <div>
      {title && (
        <div className={styles.sectionLabel} style={{ color: accent }}>{title}</div>
      )}

      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead className={styles.tableHead}>
            <tr style={{ background: thBg }}>
              {columns.map((col, i) => (
                <th
                  key={i}
                  title={col.title ?? col.label}
                  style={{
                    color:      thColor,
                    textAlign:  col.center !== false ? "center" : "left",
                    whiteSpace: col.wrap ? "normal" : "nowrap",
                    maxWidth:   col.wrap ? "64px" : undefined,
                    fontSize:   col.mono ? "11px" : undefined,
                    lineHeight: col.wrap ? "1.3" : undefined,
                    ...(i > 0 ? { borderLeft: `1px solid ${border}` } : {}),
                  }}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {!data || data.length === 0 ? (
              <tr className={styles.emptyRow}>
                <td colSpan={columns.length}>
                  {t("admin_pages.quran_report.card_titles.no_records")}
                </td>
              </tr>
            ) : (
              data.map((row, rowIdx) => (
                <tr
                  key={rowIdx}
                  className={cx(
                    rowIdx % 2 === 1 ? styles.altRow : "",
                    onRowClick       ? styles.clickableRow : "",
                  )}
                  onClick={() => onRowClick?.(row)}
                  title={onRowClick ? t("admin_pages.quran_report.table_cols.clikc_for_details") : undefined}
                >
                  {columns.map((col, colIdx) => {
                    const cellValue = row[col.key];
                    const isType    = col.key === "type";
                    const isMem     = cellValue === "Memorization";
                    const isRev     = cellValue === "Revision";

                    return (
                      <td
                        key={colIdx}
                        className={cx(
                          col.mono && styles.score,
                          col.note && styles.note,
                          col.ref  && styles.ref,
                        )}
                        style={{
                          textAlign:  col.center !== false ? "center" : "left",
                          whiteSpace: col.mono ? "nowrap" : undefined,
                          ...(colIdx > 0 ? { borderLeft: "1px solid #F5F4EF" } : {}),
                        }}
                      >
                        {isType && (isMem || isRev) ? (
                          <span className={isMem ? styles.memorization : styles.revision}>
                            {t(`${tableColsPrefix}.${cellValue.toLowerCase()}`)}
                          </span>
                        ) : (
                          cellValue ?? "—"
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {(showPagination || totalItems != null) && (
        <div className={styles.paginationRow}>
          <span className={styles.paginationTotal}>{t("admin_pages.quran_report.table_cols.total_recorded")}: {totalItems ?? 0}</span>
          {showPagination && (
            <div className={styles.paginationBtns}>
              <button
                className={styles.pageBtn}
                disabled={currentPage === 1}
                onClick={() => onPage?.(currentPage - 1)}
              >‹</button>

              {visible[0] > 1 && <span className={styles.pageSep}>…</span>}

              {visible.map(p => (
                <button
                  key={p}
                  className={cx(styles.pageBtn, p === currentPage && styles.activePage)}
                  style={p === currentPage ? { background: pagActive } : {}}
                  onClick={() => onPage?.(p)}
                >{p}</button>
              ))}

              {visible[visible.length - 1] < totalPages && (
                <span className={styles.pageSep}>…</span>
              )}

              <button
                className={styles.pageBtn}
                disabled={currentPage === totalPages}
                onClick={() => onPage?.(currentPage + 1)}
              >›</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};